import React, { useState, useCallback, useEffect, useRef } from 'react';
import type { ContainerStatus } from '../types';
import { fetchContainerLogs } from '../api/containerApi';

type AuthFetch = (url: string, options?: RequestInit) => Promise<Response>;

interface ContainerLogsModalProps {
  container: ContainerStatus;
  authFetch: AuthFetch;
  onClose: () => void;
}

const LINE_OPTIONS = [100, 500, 1000];
const SERVICE_OPTIONS = ['api', 'postgres', 'redis'];

export const ContainerLogsModal: React.FC<ContainerLogsModalProps> = ({
  container,
  authFetch,
  onClose,
}) => {
  const [service, setService] = useState<string>('api');
  const [lines, setLines] = useState(100);
  const [logs, setLogs] = useState('');
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [autoScroll, setAutoScroll] = useState(true);
  const logRef = useRef<HTMLPreElement>(null);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchContainerLogs(authFetch, container.tenant_id, service, lines);
      setLogs(data);
    } catch {
      setLogs('Failed to load logs.');
    } finally {
      setLoading(false);
    }
  }, [authFetch, container.tenant_id, service, lines]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  useEffect(() => {
    if (autoScroll && logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [logs, autoScroll]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const displayedLogs = filter
    ? logs
        .split('\n')
        .filter((line) => line.toLowerCase().includes(filter.toLowerCase()))
        .join('\n')
    : logs;

  const handleDownload = () => {
    const blob = new Blob([logs], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${container.tenant_slug}-${service}-logs.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal"
        style={{ maxWidth: '800px', width: '90%', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Logs for ${container.tenant_slug}`}
      >
        <div className="modal-header">
          <h2 style={{ fontSize: '16px' }}>
            Logs: {container.tenant_slug}
          </h2>
          <button className="btn btn-ghost" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>

        <div style={{ display: 'flex', gap: '8px', padding: '12px 20px', flexWrap: 'wrap', alignItems: 'center' }}>
          <label style={{ fontSize: '13px', opacity: 0.7 }}>
            Service:
            <select
              value={service}
              onChange={(e) => setService(e.target.value)}
              style={{ marginLeft: '4px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', padding: '4px 8px', color: 'inherit' }}
            >
              {SERVICE_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </label>

          <label style={{ fontSize: '13px', opacity: 0.7 }}>
            Lines:
            <select
              value={lines}
              onChange={(e) => setLines(Number(e.target.value))}
              style={{ marginLeft: '4px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', padding: '4px 8px', color: 'inherit' }}
            >
              {LINE_OPTIONS.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </label>

          <input
            type="text"
            placeholder="Filter logs..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{ flex: 1, minWidth: '120px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', padding: '4px 8px', color: 'inherit', fontSize: '13px' }}
            aria-label="Filter log content"
          />

          <label style={{ fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <input
              type="checkbox"
              checked={autoScroll}
              onChange={(e) => setAutoScroll(e.target.checked)}
            />
            Auto-scroll
          </label>

          <button className="btn btn-sm btn-secondary" onClick={loadLogs} disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </button>
          <button className="btn btn-sm btn-secondary" onClick={handleDownload}>
            Download
          </button>
        </div>

        <pre
          ref={logRef}
          style={{
            flex: 1,
            margin: '0 20px 20px',
            padding: '12px',
            background: 'rgba(0,0,0,0.3)',
            borderRadius: '8px',
            fontFamily: 'var(--font-mono)',
            fontSize: '12px',
            lineHeight: 1.5,
            overflow: 'auto',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
          }}
          aria-live="polite"
          aria-label="Log output"
        >
          {displayedLogs || (loading ? 'Loading logs...' : 'No logs available.')}
        </pre>
      </div>
    </div>
  );
};
